import { useState } from 'react';
import Footer from '@/components/feature/Footer';

const contactInfo = [
  {
    icon: 'ri-map-pin-2-line',
    title: 'العنوان',
    value: 'المدينة المنورة، المملكة العربية السعودية',
    sub: 'حي العزيزية، شارع الملك فهد',
    color: 'bg-emerald-50 text-emerald-600',
  },
  {
    icon: 'ri-mail-line',
    title: 'البريد الإلكتروني',
    value: 'hello@heba.sa',
    sub: 'نرد خلال 24 ساعة',
    color: 'bg-sky-50 text-sky-600',
  },
  {
    icon: 'ri-phone-line',
    title: 'الهاتف',
    value: '+966 14 000 0000',
    sub: 'الأحد – الخميس، 9ص – 5م',
    color: 'bg-amber-50 text-amber-600',
  },
  {
    icon: 'ri-twitter-x-line',
    title: 'تويتر / X',
    value: '@heba_sa',
    sub: 'تابعنا للأخبار والتحديثات',
    color: 'bg-stone-100 text-stone-700',
  },
];

const faqs = [
  {
    q: 'كيف أبدأ حملة على منصة هِبة؟',
    a: 'اضغط على "ابدأ حملتك" وأكمل الخطوات الأربع البسيطة. سيراجع فريقنا حملتك خلال 48 ساعة.',
  },
  {
    q: 'ما هي رسوم المنصة؟',
    a: 'نأخذ رسوماً إدارية بسيطة 5% فقط من المبلغ المجموع، تذهب حصراً لتغطية تكاليف التشغيل.',
  },
  {
    q: 'كيف أتأكد من مصداقية الحملات؟',
    a: 'نتحقق من هوية كل صاحب حملة ونراجع تفاصيلها قبل النشر. كما نوفر تقارير شفافة لكل حملة.',
  },
  {
    q: 'هل يمكنني استرداد تبرعي؟',
    a: 'يمكن استرداد التبرع خلال 48 ساعة من الدفع. بعد ذلك تُحوَّل الأموال لصاحب الحملة مباشرة.',
  },
];

const ContactPage = () => {
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return;
    if (form.message.length > 500) return;
    setLoading(true);
    const body = new URLSearchParams();
    body.append('name', form.name);
    body.append('email', form.email);
    body.append('subject', form.subject);
    body.append('message', form.message);
    try {
      await fetch('https://readdy.ai/api/form/d7ecc5tfi84lst2k00fg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
    } catch {
      // silent
    } finally {
      setLoading(false);
      setSubmitted(true);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50" dir="rtl">

      {/* ── Hero ── */}
      <section className="relative min-h-[45vh] flex items-center overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=modern%20Saudi%20Arabia%20office%20building%20interior%20warm%20light%20minimalist%20design%20elegant%20workspace%20Madinah%20professional%20environment%20clean%20aesthetic&width=1600&height=700&seq=701&orientation=landscape"
          alt="تواصل معنا"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-l from-black/70 via-black/50 to-black/30"></div>
        <div className="relative z-10 w-full max-w-7xl mx-auto px-8 md:px-12 pt-28 pb-16">
          <div className="max-w-xl">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-3 py-1.5 mb-5">
              <i className="ri-customer-service-2-line text-emerald-400 text-sm"></i>
              <span className="text-white/90 text-sm">نحن هنا لمساعدتك</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-black text-white leading-tight mb-4">
              تواصل <span className="text-emerald-400">معنا</span>
            </h1>
            <p className="text-white/80 text-base leading-relaxed">
              فريقنا جاهز للإجابة على استفساراتك ومساعدتك في كل ما تحتاجه.
            </p>
          </div>
        </div>
      </section>

      {/* ── Contact Info Cards ── */}
      <section className="py-14 bg-white">
        <div className="max-w-7xl mx-auto px-8 md:px-12">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {contactInfo.map((item, i) => (
              <div key={i} className="bg-stone-50 rounded-2xl border border-stone-100 p-5 hover:border-emerald-200 transition-all">
                <div className={`w-11 h-11 flex items-center justify-center rounded-xl mb-4 ${item.color}`}>
                  <i className={`${item.icon} text-lg`}></i>
                </div>
                <p className="text-stone-400 text-xs mb-1">{item.title}</p>
                <p className="text-stone-800 font-bold text-sm mb-0.5">{item.value}</p>
                <p className="text-stone-400 text-xs">{item.sub}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Form + Map ── */}
      <section className="py-16 bg-stone-50">
        <div className="max-w-7xl mx-auto px-8 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">

            {/* Form */}
            <div className="bg-white rounded-3xl border border-stone-100 p-8">
              <h2 className="text-2xl font-black text-stone-800 mb-1">أرسل رسالتك</h2>
              <p className="text-stone-400 text-sm mb-7">سنرد عليك في أقرب وقت ممكن</p>

              {submitted ? (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <div className="w-16 h-16 flex items-center justify-center bg-emerald-50 rounded-2xl mb-4">
                    <i className="ri-checkbox-circle-fill text-emerald-600 text-3xl"></i>
                  </div>
                  <h3 className="text-xl font-black text-stone-800 mb-2">تم إرسال رسالتك!</h3>
                  <p className="text-stone-500 text-sm max-w-xs">
                    شكراً لتواصلك معنا. سيرد عليك فريقنا خلال 24 ساعة على بريدك الإلكتروني.
                  </p>
                  <button
                    onClick={() => { setSubmitted(false); setForm({ name: '', email: '', subject: '', message: '' }); }}
                    className="mt-6 text-emerald-600 text-sm font-semibold hover:underline cursor-pointer whitespace-nowrap"
                  >
                    إرسال رسالة أخرى
                  </button>
                </div>
              ) : (
                <form data-readdy-form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-stone-600 text-xs font-semibold mb-1.5">الاسم الكامل *</label>
                      <input
                        type="text"
                        name="name"
                        value={form.name}
                        onChange={handleChange}
                        placeholder="محمد العمري"
                        required
                        className="w-full border border-stone-200 rounded-xl px-4 py-2.5 text-sm text-stone-800 placeholder-stone-300 focus:outline-none focus:border-emerald-400 transition-colors bg-stone-50"
                      />
                    </div>
                    <div>
                      <label className="block text-stone-600 text-xs font-semibold mb-1.5">البريد الإلكتروني *</label>
                      <input
                        type="email"
                        name="email"
                        value={form.email}
                        onChange={handleChange}
                        placeholder="example@email.com"
                        required
                        className="w-full border border-stone-200 rounded-xl px-4 py-2.5 text-sm text-stone-800 placeholder-stone-300 focus:outline-none focus:border-emerald-400 transition-colors bg-stone-50"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-stone-600 text-xs font-semibold mb-1.5">موضوع الرسالة</label>
                    <select
                      name="subject"
                      value={form.subject}
                      onChange={handleChange}
                      className="w-full border border-stone-200 rounded-xl px-4 py-2.5 text-sm text-stone-800 focus:outline-none focus:border-emerald-400 transition-colors bg-stone-50 cursor-pointer"
                    >
                      <option value="">اختر الموضوع</option>
                      <option value="استفسار عام">استفسار عام</option>
                      <option value="مشكلة تقنية">مشكلة تقنية</option>
                      <option value="حملة جديدة">حملة جديدة</option>
                      <option value="شراكة">شراكة</option>
                      <option value="شكوى">شكوى</option>
                      <option value="اقتراح">اقتراح</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-stone-600 text-xs font-semibold mb-1.5">
                      الرسالة * <span className="text-stone-300 font-normal">({form.message.length}/500)</span>
                    </label>
                    <textarea
                      name="message"
                      value={form.message}
                      onChange={handleChange}
                      placeholder="اكتب رسالتك هنا..."
                      required
                      maxLength={500}
                      rows={5}
                      className="w-full border border-stone-200 rounded-xl px-4 py-2.5 text-sm text-stone-800 placeholder-stone-300 focus:outline-none focus:border-emerald-400 transition-colors bg-stone-50 resize-none"
                    />
                    {form.message.length >= 500 && (
                      <p className="text-rose-500 text-xs mt-1">وصلت للحد الأقصى (500 حرف)</p>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={loading || form.message.length > 500}
                    className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white font-bold py-3 rounded-xl transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                  >
                    {loading ? (
                      <>
                        <i className="ri-loader-4-line animate-spin"></i>
                        جاري الإرسال...
                      </>
                    ) : (
                      <>
                        <i className="ri-send-plane-line"></i>
                        إرسال الرسالة
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>

            {/* Map + Social */}
            <div className="flex flex-col gap-5">
              {/* Map */}
              <div className="rounded-3xl overflow-hidden border border-stone-100 flex-1 min-h-64">
                <iframe
                  title="موقع منصة هبة"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3722.3!2d39.5888!3d24.5260!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x15bdbe7e5c0f7bdf%3A0x5f7c826c9a3b2e9a!2sMudhainib%2C%20Madinah%2042364!5e0!3m2!1sar!2ssa!4v1700000000000"
                  width="100%"
                  height="100%"
                  style={{ border: 0, minHeight: '260px' }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                ></iframe>
              </div>

              {/* Social Links */}
              <div className="bg-white rounded-3xl border border-stone-100 p-6">
                <h3 className="font-bold text-stone-800 mb-4 text-sm">تابعنا على منصات التواصل</h3>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { icon: 'ri-twitter-x-line', label: 'تويتر / X', handle: '@heba_sa', color: 'hover:bg-stone-100' },
                    { icon: 'ri-instagram-line', label: 'إنستغرام', handle: '@heba.sa', color: 'hover:bg-rose-50' },
                    { icon: 'ri-snapchat-line', label: 'سناب شات', handle: '@heba_sa', color: 'hover:bg-amber-50' },
                    { icon: 'ri-youtube-line', label: 'يوتيوب', handle: 'منصة هِبة', color: 'hover:bg-rose-50' },
                  ].map((s, i) => (
                    <a
                      key={i}
                      href="#"
                      className={`flex items-center gap-3 p-3 rounded-xl border border-stone-100 transition-colors cursor-pointer ${s.color}`}
                    >
                      <div className="w-8 h-8 flex items-center justify-center bg-stone-50 rounded-lg">
                        <i className={`${s.icon} text-stone-600 text-sm`}></i>
                      </div>
                      <div>
                        <p className="text-stone-700 text-xs font-semibold">{s.label}</p>
                        <p className="text-stone-400 text-xs">{s.handle}</p>
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-8 md:px-12">
          <div className="text-center mb-10">
            <span className="text-emerald-600 text-sm font-semibold tracking-wide uppercase">الأسئلة الشائعة</span>
            <h2 className="text-2xl md:text-3xl font-black text-stone-800 mt-2">أسئلة يسألها الجميع</h2>
          </div>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div
                key={i}
                className={`border rounded-2xl overflow-hidden transition-all ${
                  openFaq === i ? 'border-emerald-200 bg-emerald-50/40' : 'border-stone-100 bg-stone-50'
                }`}
              >
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between px-5 py-4 text-right cursor-pointer"
                >
                  <span className="font-semibold text-stone-800 text-sm">{faq.q}</span>
                  <div className={`w-7 h-7 flex items-center justify-center rounded-full transition-all ${
                    openFaq === i ? 'bg-emerald-600 text-white rotate-45' : 'bg-stone-200 text-stone-500'
                  }`}>
                    <i className="ri-add-line text-sm"></i>
                  </div>
                </button>
                {openFaq === i && (
                  <div className="px-5 pb-4">
                    <p className="text-stone-500 text-sm leading-relaxed">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;
